<template>
  <m-field
    :value="controller.value"
    :label="label"
    :rules="rules"
    :disabled="disabled"
    :loading="loading"
    :success="success"
    :error="error"
    :color="color"
    :dark="dark"
    :light="light"
    :hide-details="hideDetails"
    content-class="pt-0 pb-0"
    class="m-select"
    :class="{ vertical }"
  >
    <v-sheet
      :max-height="maxHeight" 
      :max-width="maxWidth"
      :class="{ x: !!maxWidth, 'pt-3': vertical&&!searchable }"
      class="m-select--container scrollable pb-2"
    >
      <v-text-field
        v-if="searchable"
        v-model="controller.search"
        :placeholder="$t('general.search_thing', { thing: '...' })"
        prepend-inner-icon="mdi-map-marker"
        clearable
        solo
        dense
        flat
        hide-details
        single-line
        class="m-select--search background px-1 pt-2 pb-1"
      >
        <template v-slot:prepend-inner>
          <v-icon class="mr-4" color="grey">
            {{ icons.search }}
          </v-icon>
        </template>
      </v-text-field>
      <div 
        v-if="native"
        class="m-select--native"
        @click="open"
      >
        <div 
          class="m-select--native-selection py-2 px-3"
        >
          {{ selection }}
        </div>
        <select 
          v-model="controller.value"
          ref="select"
          @click.stop
          @change="change"
        >
          <option 
            v-for="(option, o) in controller.options"
            :key="`m-select-option-${o}`"
            :value="option.value"
            :disabled="disabled||option.disabled"
          >
            {{ option.text }}
          </option>
        </select>
      </div>
      <v-item-group
        v-else
        :value="controller.value"
        active-class="active primary--text"
        :mandatory="mandatory"
        :multiple="multiple"
        :color="color"
        :class="{ 'flex-column justify-start': vertical }"
        class="m-select--controller d-flex"
        @change="change"
      >
        <v-item 
          v-for="(option, o) in list"
          :key="`m-select-option-${option.value}`"
          :ref="`m-select-option-${option.value}`"
          v-slot="{ active, toggle }"
          :value="option.value"
          class="m-select-option flex-grow-1 flex-shrink-1"
          :class="{ 'disabled': disabled||option.disabled }"
        >
          <v-card
            flat
            tile
            min-height="40"
            :class="{ 'flex-column justify-center': !vertical, 'pa-6': large }"
            class="option--content d-flex align-center"
            @click="toggle"
          >
            <v-icon
              v-if="checkbox||option.icon!==undefined"
              :color="active ? color : undefined"
              :large="large"
              :class="{ 'mb-4': large&&vertical, 'mr-4': large&&!vertical }"
              class="ml-4 mr-2"
            >
              {{ checkbox ? active ? multiple ? icons.checked : icons.selected : multiple ? icons.unchecked : icons.blank : option.icon }}
            </v-icon>
            <div 
              :class="[{ 'px-3': vertical, [color+'--text']: active }, vertical ? 'text-left' : 'text-center']"
              class="option--content-text py-2"
            >
              <p 
                :class="['text-'+(vertical ? 'body-2' : 'overline')]"
                class="text text-truncate mb-0"
              >{{ option.text }}</p>
              <p 
                v-if="hasCaption(option)"
                class="caption text-caption mb-0"
              >
                {{ option.caption }}
              </p>
            </div>
          </v-card>
        </v-item>
      </v-item-group>
    </v-sheet>
  </m-field>
</template>

<style lang="scss">


.m-select, .m-select-option {
  position: relative;
  // overflow: hidden;
}
.m-select {
  // .m-select--controller {
  //   width: 100%;
  // }
  select {
    display: none;
  }
  .m-select--search {
    position: sticky;
    top: 0;
    z-index: 1;
    .v-input__slot {
      background-color: transparent !important;
    }
    // background-color: var(--v-background-base);
  }

  .m-field-content {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  
  .m-select-option {
    width: 100%;
    min-width: 144px;

    .caption {
      opacity: .64;
    }

    .option--content {
      flex-shrink: 0 !important;
    }

    &.disabled {
      opacity: .4;
      pointer-events: none;
    }

  }
  
  &:not(.vertical) .option--content:not(:last-child) {
    border-right: 1px solid var(--v-grey-darken2);
  }
  
  .active .text {
    font-weight: 700 !important;
  }
}

</style>

<script>
  import {
    mdiCheckboxBlankOutline, 
    mdiCheckboxMarked,
    mdiRadioboxBlank,
    mdiRadioboxMarked,
    mdiMagnify
  } from '@mdi/js'

  export default {
    
    data: () => ({
      icons: {
        checked: mdiCheckboxMarked,
        unchecked: mdiCheckboxBlankOutline,
        blank: mdiRadioboxBlank,
        selected: mdiRadioboxMarked,
        search: mdiMagnify
      },
      controller: {
        value: null,
        options: [],
        search: null,
        scrolled: null
      }
    }),

    computed: {
      list () {
        let options = _.cloneDeep(this.controller.options);
        const query = this.controller.search;
        if (query) {
          options = _.filter(options, option => {
            return this.accentInsensitiveMatch(query, option.text);
          });
        }
        return _.orderBy(options, ['text']);
      },
      native () {
        const isMobile = this.$vuetify.breakpoint.mobile;
        return this.mobileNative && isMobile;
      },
      selection () {
        if (this.controller.value === null) return this.label || null;
        if (this.multiple) {
          return this.controller.value.map(val => {
            const option = this.controller.options.find(o => o.value === val);
            return option ? option.text : val;
          }).join(', ');
        } else {
          const option = this.controller.options.find(o => o.value === this.controller.value);
          return option ? option.text : this.controller.value;
        }
      }
    },

    watch: {
      value: {
        immediate: true,
        deep: true,
        handler (value) {
          this.controller.value = value;
          if (value&&this.controller.scrolled===null) {
            this.$nextTick(() => {
              this.scrollTo(value);
            });
          }
        }
      },
      options: {
        immediate: true,
        deep: true,
        handler (options) {
          this.controller.options = _.map(options, option => {
            option = { disabled: false, ...(_.isObject(option) ? { value: option[this.itemValue], text: option[this.itemText] } : { text: option, value: option }) }
            return option;
          });
        }
      }
    },

    methods: {
      open (e) {
        setTimeout(() => {
          this.$refs.select.click();
        }, 250);
      },

      change (selected) {
        this.$nextTick(() => {
          // console.log('select option', selected||null);
          this.$emit('input', selected);
          this.$emit('change', selected);
        });
      },

      hasCaption (option) {
        return option.hasOwnProperty('caption');
      },

      accentInsensitiveMatch (query, text) {
        const normalize = str => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        return normalize(text).includes(normalize(query));
      },

      scrollTo (value) {
        const option = this.$refs[`m-select-option-${value}`];
        if (option&&option[0]) {
          // console.log('scrolling to', option[0]);
          option[0].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.controller.scrolled = true;
        }else{
          setTimeout(() => {
            this.scrollTo(value);
          }, 100);
        }
      }

    },

    props: {
      value: {
        type: [String, Number, Array],
        default: 0
      },
      options: {
        type: [Array, Object],
        default: () => []
      },
      label: {
        type: String,
        default: undefined
      },
      color: {
        type: String,
        default: undefined
      },
      hint: {
        type: String,
        default: undefined
      },
      rules: {
        type: Array,
        default: () => []
      },
      mobileNative: {
        type: Boolean,
        default: false
      },
      dark: {
        type: Boolean,
        default: undefined
      },
      light: {
        type: Boolean,
        default: undefined
      },
      mandatory: {
        type: Boolean,
        default: false
      },
      checkbox: {
        type: Boolean,
        default: true
      },
      multiple: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      searchable: {
        type: Boolean,
        default: false
      },
      success: {
        type: Boolean,
        default: undefined
      },
      error: {
        type: Boolean,
        default: undefined
      },
      itemValue: {
        type: String,
        default: 'value'
      },
      itemText: {
        type: String,
        default: 'text'
      },
      hideDetails: {
        type: [Boolean, String],
        default: false
      },
      vertical: {
        type: Boolean,
        default: false
      },
      large: {
        type: Boolean,
        default: false
      },
      maxHeight: {
        type: [String, Number],
        default: undefined
      },
      maxWidth: {
        type: [String, Number],
        default: undefined
      }
    },

    components: {
      MField: () => import('@/components/mField'),
    },
  }
</script>